import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from '@headlessui/react';
import { TableCellsIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { analysisCategories } from '../../services/enrichmentService';

const ExportDialog = ({
  isOpen = false,
  onClose = () => {},
  onExport = () => {},
  variablePresets = [],
  marketAreas = [],
  isExporting = false,
}) => {
  // Filter to only show 2025 presets (exclude presets with '2024' in the name) - memoized to prevent infinite loops
  const filteredVariablePresets = useMemo(() =>
    variablePresets.filter(preset =>
      preset.name && !preset.name.includes('2024')
    ),
    [variablePresets]
  );

  const [fileName, setFileName] = useState('');
  const [includeUSAData, setIncludeUSAData] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState(
    () => new Set(marketAreas.map((area) => area.id))
  );

  /**
   * Helper function to determine the tier of a preset (1, 2, or 3)
   * Returns the tier number for sorting purposes
   */
  const getPresetTier = (preset) => {
    if (!preset) return 999; // Unknown presets go last

    // Check if tier property is explicitly set
    if (preset.tier) return preset.tier;

    // Normalize the preset name for comparison
    const normalizedName = preset.name ? preset.name.toLowerCase().trim() : '';

    // Check for Tier 1 patterns
    const tier1Patterns = [
      'core variables (tier 1)',
      'core variables tier 1',
      'tier 1 core variables',
      'tier 1 - core variables',
      (name) => name.includes('core') && name.includes('variables') && name.includes('tier 1'),
      (name) => name.includes('core') && name.includes('variables') && name.includes('tier1'),
      (name) => name.startsWith('core variables') && name.includes('1'),
      (name) => name.includes('tier 1 core'),
      (name) => name.includes('tier1 core'),
    ];

    for (const pattern of tier1Patterns) {
      if (typeof pattern === 'string') {
        const cleanedName = normalizedName.replace(/\s*\(\d+\s+variables?\).*$/i, '').trim();
        if (cleanedName === pattern) return 1;
      } else if (typeof pattern === 'function') {
        if (pattern(normalizedName)) return 1;
      }
    }

    // Check for Tier 2 patterns
    if (normalizedName.includes('tier 2') || normalizedName.includes('tier2')) {
      return 2;
    }

    // Check for Tier 3 patterns (retail/spending variables)
    if (normalizedName.includes('tier 3') ||
        normalizedName.includes('tier3') ||
        normalizedName.includes('retail') ||
        normalizedName.includes('spending')) {
      return 3;
    }

    // Check group/category properties
    if (preset.group || preset.category || preset.tierGroup) {
      const groupText = (preset.group || preset.category || preset.tierGroup || '').toLowerCase();
      if (groupText.includes('tier 1') || (groupText.includes('core') && groupText.includes('tier 1'))) {
        return 1;
      }
      if (groupText.includes('tier 2')) return 2;
      if (groupText.includes('tier 3') || groupText.includes('retail')) return 3;
    }

    return 999; // Unknown tier goes last
  };

  /**
   * Helper function to determine if a preset is a Core Variables (Tier 1) preset
   * Checks multiple patterns to ensure proper categorization
   */
  const isCoreVariablesTier1Preset = (preset) => {
    return getPresetTier(preset) === 1;
  };

  // Split presets into core (Tier 1 Core) and additional variables using improved logic
  // Sort them by tier to ensure proper display order - memoized to prevent unnecessary recalculations
  const corePresets = useMemo(() =>
    filteredVariablePresets
      .filter(preset => isCoreVariablesTier1Preset(preset))
      .sort((a, b) => getPresetTier(a) - getPresetTier(b)),
    [filteredVariablePresets]
  );

  const additionalPresets = useMemo(() =>
    filteredVariablePresets
      .filter(preset => !isCoreVariablesTier1Preset(preset))
      .sort((a, b) => getPresetTier(a) - getPresetTier(b)),
    [filteredVariablePresets]
  );

  // Initialize core presets as selected by default
  const [selectedCorePresets, setSelectedCorePresets] = useState(
    () => new Set(corePresets.map(preset => preset.id))
  );
  const [selectedAdditionalPresets, setSelectedAdditionalPresets] = useState(new Set());

  useEffect(() => {
    if (marketAreas.length > 0) {
      const firstArea = marketAreas[0];
      const projectNumber =
        firstArea?.project_number || 
        firstArea?.project?.project_number || 
        firstArea?.project_id || 
        '00000.00';
  
      const date = new Date();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
  
      const fileName = `${projectNumber} Esri Data ${month}.${day}.${year}`;
      setFileName(fileName);
    } else {
      setFileName('00000.00 Esri Data 00.00.00');
    }
  }, [marketAreas]);

  useEffect(() => {
    setSelectedAreas(new Set(marketAreas.map((area) => area.id)));
  }, [marketAreas]);

  // Update selectedCorePresets when corePresets changes
  useEffect(() => {
    setSelectedCorePresets(new Set(corePresets.map(preset => preset.id)));
  }, [corePresets]);

  const handleSelectAllAreas = (e) => {
    if (e.target.checked) {
      setSelectedAreas(new Set(marketAreas.map((area) => area.id)));
    } else {
      setSelectedAreas(new Set());
    }
  };

  const handleAreaSelection = (areaId) => {
    setSelectedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const handleIncludeUSACheckbox = (e) => {
    setIncludeUSAData(e.target.checked);
  };

  const handlePresetSelection = (presetId, isCore) => {
    const setterFunction = isCore ? setSelectedCorePresets : setSelectedAdditionalPresets;
    setterFunction((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  };

  // Helper function to sort variables according to analysisCategories order
  const sortVariablesByAnalysisOrder = (variables) => {
    // Build a list of all variables in the order they appear in analysisCategories
    const allVariablesInOrder = [];
    Object.values(analysisCategories).forEach(category => {
      category.variables.forEach(variable => {
        allVariablesInOrder.push(variable.id);
      });
    });

    // Sort the variables array according to their position in analysisCategories
    return variables.sort((a, b) => {
      const indexA = allVariablesInOrder.indexOf(a);
      const indexB = allVariablesInOrder.indexOf(b);

      // If a variable is not found in analysisCategories, put it at the end
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  };

  const handleSubmitExport = async (e) => {
    e.preventDefault();

    if (isExporting) return;

    try {
      const selectedMarketAreas = marketAreas.filter((area) =>
        selectedAreas.has(area.id)
      );

      const selectedPresetIds = new Set([...selectedCorePresets, ...selectedAdditionalPresets]);

      // Sort presets by tier (1, 2, 3) before collecting variables
      // This ensures the export follows the order: Tier 1 → Tier 2 → Tier 3
      const sortedPresets = filteredVariablePresets
        .filter(preset => selectedPresetIds.has(preset.id))
        .sort((a, b) => getPresetTier(a) - getPresetTier(b));

      const variables = sortedPresets.flatMap(preset => preset.variables || []);

      // Sort variables according to their order in analysisCategories
      const sortedVariables = sortVariablesByAnalysisOrder(variables);
  
      const baseFileName = fileName.replace(/\.(xlsx|csv)$/i, '').trim();
      const fileNameWithExt = `${baseFileName}.xlsx`;

      const result = await onExport({
        variables: sortedVariables,
        selectedMarketAreas,
        fileName: fileNameWithExt,
        includeUSAData: Boolean(includeUSAData),
        vintageYear: '2025',
      });
  
      if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNameWithExt;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed: ' + (error.message || 'Unknown error'));
    }
  };

  const isExportDisabled =
    isExporting ||
    (selectedCorePresets.size === 0 && selectedAdditionalPresets.size === 0) ||
    selectedAreas.size === 0 ||
    selectedAreas.size > 50;

  const exportButtonText = isExporting ? 'Exporting...' : 'Export';

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <TableCellsIcon className="h-5 w-5" />
              Export Enriched Data
            </Dialog.Title>
            <button
              onClick={onClose}
              disabled={isExporting}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeUSAData}
                  onChange={handleIncludeUSACheckbox}
                  disabled={isExporting}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded
                           disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  Include USA Data
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Export File Name
              </label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                disabled={isExporting}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 
                         bg-white dark:bg-gray-700 py-2 px-3 shadow-sm focus:border-blue-500 
                         focus:outline-none focus:ring-blue-500 dark:text-white
                         disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter file name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Market Areas to Include {selectedAreas.size > 50 && 
                  <span className="text-red-500 ml-1">(Maximum 50 areas allowed)</span>
                }
              </label>
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 max-h-40 overflow-y-auto">
                <label className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedAreas.size === marketAreas.length}
                    onChange={handleSelectAllAreas}
                    disabled={isExporting}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">
                    Select All
                  </span>
                </label>
                {marketAreas.map((area) => (
                  <label
                    key={area.id}
                    className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAreas.has(area.id)}
                      onChange={() => handleAreaSelection(area.id)}
                      disabled={isExporting}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      {area.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Core Variables Section - Only show if there are core presets */}
            {corePresets.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Core Variables (Tier 1)
                </label>
                <div className="space-y-2">
                  {corePresets.map((preset) => (
                    <label key={preset.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedCorePresets.has(preset.id)}
                        onChange={() => handlePresetSelection(preset.id, true)}
                        disabled={isExporting}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded
                                 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                        {preset.name} ({preset.variables?.length || 0} variables)
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Variables Section - Always show if there are additional presets */}
            {additionalPresets.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {corePresets.length > 0 ? 'Add Additional Variables' : 'Variables'}
                </label>
                <div className="space-y-2">
                  {additionalPresets.map((preset) => (
                    <label key={preset.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedAdditionalPresets.has(preset.id)}
                        onChange={() => handlePresetSelection(preset.id, false)}
                        disabled={isExporting}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded
                                 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                        {preset.name} ({preset.variables?.length || 0} variables)
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Show message if no presets available */}
            {filteredVariablePresets.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No variable presets available for 2024
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 text-sm font-medium text-gray-900 bg-white border 
                       border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 
                       dark:text-white dark:border-gray-600 dark:hover:bg-gray-600
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitExport}
              disabled={isExportDisabled}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white 
                       bg-blue-600 rounded-md hover:bg-blue-700 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       min-w-[80px] justify-center"
            >
              {exportButtonText}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ExportDialog;